import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tr,
  TableContainer,
  useToast,
  Tag,
  TagLabel,
  Checkbox,
} from "@chakra-ui/react";
import { FiFilter, FiChevronUp, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";

const API_URL = import.meta.env.VITE_API_URL;

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString();
    }
  }

  if (Array.isArray(value)) {
    if (!value.length) return "Empty";
    if (typeof value[0] !== "object") {
      return value.slice(0, 3).join(", ") + (value.length > 3 ? `... +${value.length - 3}` : "");
    }
    return `${value.length} items`;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null)
      .map(([key, entryValue]) => {
        const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();
        if (Array.isArray(entryValue)) return `${label}: ${entryValue.length} items`;
        if (typeof entryValue === "boolean") return `${label}: ${entryValue ? "Yes" : "No"}`;
        if (typeof entryValue === "object") return `${label}: Object`;
        return `${label}: ${entryValue}`;
      })
      .slice(0, 4);

    return entries.length ? entries.join(", ") : "Object";
  }

  return String(value);
};

const isCompactColumn = (columnKey) => {
  const key = (columnKey || "").toLowerCase();
  return (
    key === "_id" ||
    key.endsWith("id") ||
    key === "createdby" ||
    key === "updatedby" ||
    key === "reportname"
  );
};

const ViewReport = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [perPage, setPerPage] = useState("09");
  const [currentPage, setCurrentPage] = useState(1);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState({});
  const [accessMap, setAccessMap] = useState({});
  const [showFilters, setShowFilters] = useState(true);

  // ── Checkbox selection state ──────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  const breadcrumbItems = [
    { title: "Report", link: "/stats" },
    { title: "View all reports", link: "/reports/view" },
  ];

  const columns = useMemo(() => {
    if (!reports.length) return [];
    const hiddenColumns = ["__v", "accessControl", "selectedColumns", "selectedTables", "createdBy", "sharedBy", "sharedDate"];
    return Object.keys(reports[0]).filter((key) => !hiddenColumns.includes(key));
  }, [reports]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "") || "";
      const response = await axios.get(`${API_URL}/report/my-access?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${cleanToken}` },
      });
      const reportsData = response.data.data || [];
      setReports(reportsData);
      setTotalRecords(reportsData.length);
      // Clear selection when data refreshes
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast({
        title: "Error loading reports",
        description: error.response?.data?.message || "Failed to load reports.",
        status: "error",
        duration: 4000,
      });
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [currentPage, perPage]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchReports();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const fetchAccess = async (reportId) => {
    const token = localStorage.getItem("prima_session");
    const res = await axios.get(`${API_URL}/report/access/${reportId}?_t=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data?.data || {};
  };

  useEffect(() => {
    const loadAccess = async () => {
      try {
        const entries = await Promise.all(
          reports
            .filter((report) => report?._id)
            .map(async (report) => {
              const access = await fetchAccess(report._id);
              return [report._id, access];
            }),
        );
        setAccessMap(Object.fromEntries(entries));
      } catch (error) {
        console.error("Error fetching access map:", error);
        setAccessMap({});
      }
    };
    if (reports.length) loadAccess();
    else setAccessMap({});
  }, [reports]);

  const currentUserId = useMemo(() => {
    try {
      const userStr = localStorage.getItem("prima_user");
      if (!userStr) return null;
      const user = JSON.parse(userStr);
      return user?._id || user?.id || null;
    } catch { return null; }
  }, []);

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filteredReports = reports.filter((row) => {
    const access = accessMap[row._id];
    if (access?.privateAccess === true && row.createdBy !== currentUserId) return false;
    return columns.every((columnKey) => {
      const query = (searchFilters[columnKey] || "").toString().toLowerCase().trim();
      if (!query) return true;
      const cellValue = formatValue(getNestedValue(row, columnKey));
      return String(cellValue).toLowerCase().includes(query);
    });
  });

  // ── Paginated rows (for display + page-level select-all) ──────────────────
  const pageSize = parseInt(perPage, 10);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // IDs visible on the current page
  const pageIds = paginatedReports.map((r) => r._id).filter(Boolean);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id)) && !allPageSelected;

  const toggleSelectAll = () => {
    if (allPageSelected) {
      // Deselect all on this page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all on this page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── CSV Export ─────────────────────────────────────────────────────────────
  /**
   * If any rows are selected → export only selected rows (from ALL pages).
   * Otherwise fall back to exporting all filtered rows.
   */
  const exportCSV = () => {
    if (selectedIds.size === 0) {
      toast({
        title: "No reports selected",
        description: "Please select at least one report to export.",
        status: "warning",
        duration: 2500,
      });
      return;
    }

    const rowsToExport = filteredReports.filter((r) => r._id && selectedIds.has(r._id));

    const exportColumns = ["srNo", ...columns, "accessType"];
    const headerLine = exportColumns.join(",");

    const lines = rowsToExport.map((row, index) => {
      const reportAccess = row.accessControl || {};
      const accessType = reportAccess.publicAccess
        ? "Public"
        : reportAccess.privateAccess
          ? "Private"
          : "Shared";

      const values = [
        index + 1,
        ...columns.map((col) => {
          const value = formatValue(getNestedValue(row, col));
          return `"${String(value).replace(/"/g, '""')}"`;
        }),
        `"${accessType}"`,
      ];
      return values.join(",");
    });

    const csv = [headerLine, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `reports-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: `Exported ${rowsToExport.length} report${rowsToExport.length !== 1 ? "s" : ""}`,
      status: "success",
      duration: 2500,
    });
  };

  if (loading) return <Loading />;

  return (
    <Box p={{ base: 4, md: 6 }} bg="#F7F9FB" minH="100vh">
      <BreadCrumbMaker items={breadcrumbItems} />

      <Flex justify="space-between" align="center" mt={6} mb={6}>
        <Heading size="lg" color="#303A6D">
          All Reports
        </Heading>
        <Button
          bg="#3E6AE1"
          color="white"
          _hover={{ bg: "#355fd0" }}
          onClick={() => navigate("/reports/create")}
        >
          + Create New Report
        </Button>
      </Flex>

      <Flex justify="space-between" align="center" mb={4}>
        {/* Selection badge */}
        <Flex align="center" gap={2}>
          {selectedIds.size > 0 && (
            <Tag colorScheme="blue" size="md">
              <TagLabel>{selectedIds.size} selected</TagLabel>
            </Tag>
          )}
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              color="gray.500"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </Button>
          )}
        </Flex>

        <Flex gap={3}>
          <Button
            leftIcon={<FiFilter />}
            variant="outline"
            bg="white"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <Menu>
            <MenuButton as={Button} rightIcon={<FiChevronUp />} variant="outline" bg="white">
              Action
            </MenuButton>
            <MenuList>
              <MenuItem onClick={exportCSV} isDisabled={selectedIds.size === 0}>
                {selectedIds.size > 0
                  ? `Export Selected (${selectedIds.size}) as CSV`
                  : "Export as CSV (select rows first)"}
              </MenuItem>
              <MenuItem onClick={() => fetchReports()}>Refresh Reports</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="#D9E0F0" borderRadius="16px" overflow="hidden">
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr bg="#FFFFFF">
                {/* ── Select-all checkbox ── */}
                <Th w="50px">
                  <Checkbox
                    isChecked={allPageSelected}
                    isIndeterminate={somePageSelected}
                    onChange={toggleSelectAll}
                    colorScheme="blue"
                    borderColor="#CBD5E0"
                  />
                </Th>
                <Th color="#6A7DAE" fontSize="12px" fontWeight="600">Sr. No</Th>
                {columns.map((column) => (
                  <Th key={column} color="#6A7DAE" fontSize="12px" fontWeight="600">
                    {column.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim()}
                  </Th>
                ))}
                <Th color="#6A7DAE" fontSize="12px" fontWeight="600">Access Control</Th>
                <Th color="#6A7DAE" fontSize="12px" fontWeight="600">Actions</Th>
              </Tr>

              {showFilters && (
                <Tr bg="#E8F0FF">
                  <Th />
                  <Th />
                  {columns.map((column) => (
                    <Th key={`${column}-search`}>
                      <Input
                        size="sm"
                        placeholder="Search"
                        bg="white"
                        border="1px solid"
                        borderColor="#D7DEEE"
                        _placeholder={{ color: "#A0AEC0" }}
                        value={searchFilters[column] || ""}
                        onChange={(e) =>
                          setSearchFilters((prev) => ({ ...prev, [column]: e.target.value }))
                        }
                      />
                    </Th>
                  ))}
                  <Th />
                  <Th />
                </Tr>
              )}
            </Thead>

            <Tbody>
              {paginatedReports.length > 0 ? (
                paginatedReports.map((row, index) => {
                  const isSelected = row._id ? selectedIds.has(row._id) : false;
                  return (
                    <Tr
                      key={row._id || index}
                      bg={isSelected ? "blue.50" : undefined}
                      _hover={{ bg: isSelected ? "blue.100" : "#F7FAFC" }}
                      cursor="pointer"
                      onClick={() =>
                        row._id && navigate(`/reports/view/${row._id}`, { state: { report: row } })
                      }
                    >
                      {/* ── Per-row checkbox ── */}
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          isChecked={isSelected}
                          colorScheme="blue"
                          borderColor="#CBD5E0"
                          onChange={() => row._id && toggleRow(row._id)}
                        />
                      </Td>
                      <Td>{(currentPage - 1) * pageSize + index + 1}</Td>
                      {columns.map((col) => {
                        const value = formatValue(getNestedValue(row, col));
                        const compact = isCompactColumn(col);
                        return (
                          <Td
                            key={col}
                            title={String(value)}
                            maxW={compact ? "180px" : "280px"}
                            whiteSpace={compact ? "nowrap" : "normal"}
                            wordBreak={compact ? "normal" : "break-word"}
                            overflow={compact ? "hidden" : "visible"}
                            textOverflow={compact ? "ellipsis" : "clip"}
                          >
                            {value}
                          </Td>
                        );
                      })}

                      {/* Access Control Column */}
                      <Td>
                        {(() => {
                          const reportAccess = row.accessControl || {};
                          if (reportAccess.publicAccess === true) {
                            return (
                              <Tag size="sm" colorScheme="blue">
                                <TagLabel>Public</TagLabel>
                              </Tag>
                            );
                          }
                          if (reportAccess.privateAccess === true) {
                            return (
                              <Tag size="sm" colorScheme="red">
                                <TagLabel>Private</TagLabel>
                              </Tag>
                            );
                          }
                          const userCount = (reportAccess.userAccess || []).length;
                          const roleCount = (reportAccess.roleAccess || []).length;
                          return (
                            <Flex gap={1} wrap="wrap">
                              <Tag size="sm" colorScheme="purple">
                                <TagLabel>Shared</TagLabel>
                              </Tag>
                              {roleCount > 0 && (
                                <Tag size="sm" colorScheme="orange">
                                  <TagLabel>{roleCount} role{roleCount !== 1 ? "s" : ""}</TagLabel>
                                </Tag>
                              )}
                              {userCount > 0 && (
                                <Tag size="sm" colorScheme="cyan">
                                  <TagLabel>{userCount} user{userCount !== 1 ? "s" : ""}</TagLabel>
                                </Tag>
                              )}
                            </Flex>
                          );
                        })()}
                      </Td>

                      <Td>
                        <Flex gap={2} onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            isDisabled={!accessMap[row._id]?.canEdit}
                            onClick={() =>
                              navigate(`/reports/edit/${row._id}`, { state: { report: row } })
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            isDisabled={!accessMap[row._id]?.canRun}
                            onClick={() =>
                              navigate(`/reports/view/${row._id}`, { state: { report: row } })
                            }
                          >
                            Run
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={columns.length + 4} py={12} textAlign="center" color="gray.500">
                    No reports available.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex justify="space-between" align="center" p={4} borderTop="1px solid" borderColor="#E2E8F0">
          <Flex align="center" gap={3} color="#7A869A" fontWeight="500">
            <Text>Showing</Text>
            <Select
              w="80px"
              bg="white"
              value={perPage}
              onChange={(e) => {
                setPerPage(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="09">09</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </Select>
            <Text>of {filteredReports.length}</Text>
          </Flex>

          <Flex align="center" gap={1}>
            <IconButton
              aria-label="previous"
              icon={<FiChevronLeft />}
              size="sm"
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            />
            {[...Array(Math.min(5, Math.ceil(filteredReports.length / pageSize)))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <Button
                  key={pageNum}
                  size="sm"
                  bg={currentPage === pageNum ? "#3E6AE1" : "white"}
                  color={currentPage === pageNum ? "white" : "#303A6D"}
                  _hover={{ bg: currentPage === pageNum ? "#355fd0" : "#F7FAFC" }}
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <IconButton
              aria-label="next"
              icon={<FiChevronRight />}
              size="sm"
              isDisabled={currentPage >= Math.ceil(filteredReports.length / pageSize)}
              onClick={() => setCurrentPage(currentPage + 1)}
            />
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default ViewReport;